/**
 * Organization Context Schema
 *
 * This defines the structure for the JSONB context field in the organizations table.
 * The schema is versioned to allow for future migrations.
 */
// Default empty context (V1 for backward compatibility)
export const DEFAULT_ORGANIZATION_CONTEXT = {
    schema_version: "v1",
    business_name: "",
    business_description: "",
    industries: [],
    mission: "",
    geography: {
        countries: [],
        regions: [],
        cities: [],
    },
    online_business: false,
    solutions: [],
    target_audience: [],
    brand_values: [],
    tone_of_voice: "",
    branding: {
        primary_color: "#3b82f6",
        secondary_color: "#64748b",
    },
};
// Default V2 context with organization terminology
export const DEFAULT_ORGANIZATION_CONTEXT_V2 = {
    schema_version: "v2",
    organization_name: "",
    organization_description: "",
    industries: [],
    mission: "",
    geography: {
        countries: [],
        regions: [],
        cities: [],
    },
    online_business: false,
    solutions: [],
    target_audience: [],
    brand_values: [],
    tone_of_voice: "",
    branding: {
        primary_color: "#3b82f6",
        secondary_color: "#64748b",
    },
};
// Type guards
export function isOrganizationContextV1(context) {
    return context && context.schema_version === "v1";
}
export function isOrganizationContextV2(context) {
    return context && context.schema_version === "v2";
}
export function isValidOrganizationContext(context) {
    return isOrganizationContextV1(context) || isOrganizationContextV2(context);
}
// Helper to ensure context has proper structure
export function normalizeOrganizationContext(context) {
    if (isOrganizationContextV2(context)) {
        return context;
    }
    if (isOrganizationContextV1(context)) {
        return migrateOrganizationContextToV2(context);
    }
    // If context is empty or invalid, return default V2 (preferred for new contexts)
    return DEFAULT_ORGANIZATION_CONTEXT_V2;
}
// Migration helper to convert V1 to V2
export function migrateOrganizationContextToV2(context) {
    // Handle hybrid case where schema_version is v1 but fields are already v2
    const hasV2Fields = "organization_name" in context && "organization_description" in context;
    const hasV1Fields = "business_name" in context && "business_description" in context;
    if (hasV2Fields) {
        // Already has V2 field names, just update schema version
        return {
            ...context,
            schema_version: "v2",
        };
    }
    else if (hasV1Fields) {
        // True V1 format, migrate field names
        return {
            schema_version: "v2",
            organization_name: context.business_name,
            organization_description: context.business_description,
            industries: context.industries,
            mission: context.mission,
            geography: context.geography,
            online_business: context.online_business,
            solutions: context.solutions,
            target_audience: context.target_audience,
            brand_values: context.brand_values,
            tone_of_voice: context.tone_of_voice,
            branding: context.branding,
        };
    }
    else {
        // Fallback to default V2 context
        return DEFAULT_ORGANIZATION_CONTEXT_V2;
    }
}
